import * as Sentry from "@sentry/react";
import React from "react";
import { useSearchParams } from "react-router-dom";

import { Tabs } from "@performyard/py-components";

import { AdminContainer, AdminTitle } from "../../../../components/Admin";
import PageTitle from "../../../../components/PageTitle";
import Spacer from "../../../../components/Spacer";
import AIInsightsEmployeesSettingsTab from "./Employees";
import AIInsightsEngagementSettingsTab from "./Engagement";
import AIInsightsMeetingsSettingsTab from "./Meetings";
import AIInsightsReviewsSettingsTab from "./Reviews";
import AIInsightsSurveysSettingsTab from "./Surveys";

import { getFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";
import { useAiInsightsVisibility } from "../../../../hooks/useFeatureFlag/useAiInsightsVisibility";

import { companyAISettingsQuery, currentUserQuery } from "../../../../api/queries";
import { dataLoader } from "../../../../api/queries/dataLoader";

import ErrorPage from "../../../Error";

export const aIInsightsSettingsLoader = () => {
	const queries = [companyAISettingsQuery, getFeatureFlagQuery];

	return dataLoader([[currentUserQuery], queries]);
};

const AIInsightsSettings = () => {
	const [searchParams] = useSearchParams();

	const { tabs: tabVisibility } = useAiInsightsVisibility();

	const tabs = [
		...(tabVisibility.reviews
			? [{ value: "reviews", label: "Reviews", content: <AIInsightsReviewsSettingsTab /> }]
			: []),
		...(tabVisibility.engagement
			? [{ value: "engagement", label: "Engagement", content: <AIInsightsEngagementSettingsTab /> }]
			: []),
		...(tabVisibility.meetings
			? [{ value: "meetings", label: "Meetings", content: <AIInsightsMeetingsSettingsTab /> }]
			: []),
		...(tabVisibility.surveys
			? [{ value: "surveys", label: "Surveys", content: <AIInsightsSurveysSettingsTab /> }]
			: []),
		...(tabVisibility.employees
			? [{ value: "employees", label: "Employees", content: <AIInsightsEmployeesSettingsTab /> }]
			: []),
	];

	// No visible tabs means the company is on neither the AI nor the coaching package, or the user is force browsing.
	if (tabs.length === 0) {
		return <ErrorPage code={404} />;
	}

	// Set the active tab based on the URL parameter
	// If the URL parameter is not a valid tab, default to first valid tab
	const validTabValues = tabs.map((tab) => tab.value);
	const tabFromUrl = searchParams.get("tab");
	const activeTab = tabFromUrl && validTabValues.includes(tabFromUrl) ? tabFromUrl : tabs[0].value;

	return (
		<Sentry.ErrorBoundary fallback={undefined}>
			<Spacer height={4} />
			<AdminContainer>
				<PageTitle title="AI Insights" />
				<AdminTitle>AI Insights</AdminTitle>
				<Spacer height={3} />
				<Tabs
					tabs={tabs}
					value={activeTab}
					urlParam="tab"
					color="neutral"
					size="large"
					dangerouslySetClassName="gap-10"
				/>
			</AdminContainer>
		</Sentry.ErrorBoundary>
	);
};

export default AIInsightsSettings;
